import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PlanRoadmapView } from './PlanRoadmapView';
import { usePlanStore } from '@/stores/ai/plan-store';
import { FOREST_GREEN, MID_GRAY, BORDER, ERROR, WHITE } from '@/styles/tokens';
import { Clock, Trash2, Play, Loader2, BookOpen } from 'lucide-react';
import type { Roadmap, AuditResult, SavedRoadmapSummary } from '@/types/plan';

interface PlanRoadmapModalProps {
  isOpen: boolean;
  onClose: () => void;
  roadmap?: Roadmap;
  auditResult?: AuditResult;
  onContinue?: () => void;
  onRefineWithChat?: () => void;
  onModifyPhase?: (phaseId: string) => void;
  onDiscard?: () => void;
}

export function PlanRoadmapModal({
  isOpen,
  onClose,
  roadmap,
  auditResult,
  onContinue,
  onRefineWithChat,
  onModifyPhase,
  onDiscard,
}: PlanRoadmapModalProps) {
  const savedRoadmaps = usePlanStore((s) => s.savedRoadmaps);
  const savedRoadmapsLoaded = usePlanStore((s) => s.savedRoadmapsLoaded);
  const loadSavedRoadmaps = usePlanStore((s) => s.loadSavedRoadmaps);
  const restoreRoadmap = usePlanStore((s) => s.restoreRoadmap);
  const deleteSavedRoadmap = usePlanStore((s) => s.deleteSavedRoadmap);
  const saveCurrentRoadmap = usePlanStore((s) => s.saveCurrentRoadmap);

  const [activeTab, setActiveTab] = useState<string>(roadmap ? 'current' : 'saved');
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Load saved roadmaps when modal opens
  useEffect(() => {
    if (isOpen && !savedRoadmapsLoaded) {
      loadSavedRoadmaps();
    }
  }, [isOpen, savedRoadmapsLoaded, loadSavedRoadmaps]);

  // Reset tab when modal opens based on whether we have a current roadmap
  useEffect(() => {
    if (isOpen) {
      setActiveTab(roadmap ? 'current' : 'saved');
    }
  }, [isOpen, roadmap]);

  const handleRestore = async (id: string) => {
    setRestoringId(id);
    await restoreRoadmap(id);
    setRestoringId(null);
    onClose();
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    await deleteSavedRoadmap(id);
    setDeletingId(null);
  };

  const handleSave = async () => {
    await saveCurrentRoadmap();
  };

  const hasCurrent = !!roadmap;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent variant="light" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle style={{ color: '#1F2937' }}>Roadmaps</DialogTitle>
        </DialogHeader>

        <div style={{ padding: '0 24px 24px' }}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full grid grid-cols-2" style={{ marginBottom: 12 }}>
              {hasCurrent && (
                <TabsTrigger value="current" className="flex-1 text-xs">
                  Current Roadmap
                </TabsTrigger>
              )}
              <TabsTrigger
                value="saved"
                className={hasCurrent ? 'flex-1 text-xs' : 'col-span-2 text-xs'}
              >
                Saved Roadmaps
                {savedRoadmaps.length > 0 && (
                  <span style={{
                    marginLeft: 6,
                    fontSize: 10,
                    background: `${FOREST_GREEN.DEFAULT}15`,
                    color: FOREST_GREEN.DEFAULT,
                    padding: '1px 6px',
                    borderRadius: 8,
                  }}>
                    {savedRoadmaps.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Current roadmap tab */}
            {hasCurrent && (
              <TabsContent value="current" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={handleSave}
                    style={{
                      fontSize: 12,
                      padding: '4px 12px',
                      borderRadius: 4,
                      border: `1px solid ${BORDER.DEFAULT}`,
                      background: 'none',
                      cursor: 'pointer',
                      color: MID_GRAY.DEFAULT,
                    }}
                  >
                    Save Roadmap
                  </button>
                </div>
                <PlanRoadmapView
                  roadmap={roadmap}
                  auditResult={auditResult}
                  onContinue={onContinue!}
                  onRefineWithChat={onRefineWithChat!}
                  onModifyPhase={onModifyPhase!}
                  onDiscard={onDiscard!}
                />
              </TabsContent>
            )}

            {/* Saved roadmaps tab */}
            <TabsContent value="saved" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              {!savedRoadmapsLoaded ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: MID_GRAY.DEFAULT }}>
                  <Loader2 className="animate-spin" style={{ width: 20, height: 20, margin: '0 auto 8px' }} />
                  <div style={{ fontSize: 13 }}>Loading saved roadmaps...</div>
                </div>
              ) : savedRoadmaps.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: MID_GRAY.lighter }}>
                  <BookOpen style={{ width: 28, height: 28, margin: '0 auto 8px', opacity: 0.5 }} />
                  <div style={{ fontSize: 13 }}>No saved roadmaps yet</div>
                  <div style={{ fontSize: 11, marginTop: 4 }}>
                    Roadmaps are automatically saved when generated
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {savedRoadmaps.map((item: SavedRoadmapSummary) => (
                    <SavedRoadmapCard
                      key={item.id}
                      item={item}
                      isRestoring={restoringId === item.id}
                      isDeleting={deletingId === item.id}
                      onRestore={() => handleRestore(item.id)}
                      onDelete={() => handleDelete(item.id)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Saved Roadmap Card ───────────────────────────────────────────────────

function SavedRoadmapCard({
  item,
  isRestoring,
  isDeleting,
  onRestore,
  onDelete,
}: {
  item: SavedRoadmapSummary;
  isRestoring: boolean;
  isDeleting: boolean;
  onRestore: () => void;
  onDelete: () => void;
}) {
  const formattedDate = (() => {
    try {
      return new Date(item.createdAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return item.createdAt;
    }
  })();

  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 8,
        border: `1px solid ${BORDER.DEFAULT}`,
        background: WHITE,
      }}
    >
      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: '#1F2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.title}
        </span>
        {item.auditScore != null && (
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: item.auditScore >= 80 ? FOREST_GREEN.DEFAULT : item.auditScore >= 60 ? '#D97706' : ERROR.DEFAULT,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {item.auditScore}
          </span>
        )}
      </div>

      {/* Meta row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: MID_GRAY.lighter }}>
        <span>{item.phaseCount} phase{item.phaseCount !== 1 ? 's' : ''}</span>
        <span>·</span>
        <Clock style={{ width: 11, height: 11 }} />
        <span>{formattedDate}</span>
        <span style={{ flex: 1 }} />

        {/* Actions */}
        <button
          type="button"
          disabled={isRestoring}
          onClick={onRestore}
          title="Restore this roadmap"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 11,
            padding: '3px 8px',
            borderRadius: 4,
            border: `1px solid ${FOREST_GREEN.DEFAULT}30`,
            background: `${FOREST_GREEN.DEFAULT}08`,
            color: FOREST_GREEN.DEFAULT,
            cursor: isRestoring ? 'wait' : 'pointer',
          }}
        >
          {isRestoring ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <Play style={{ width: 12, height: 12 }} />}
          Implement
        </button>
        <button
          type="button"
          disabled={isDeleting}
          onClick={onDelete}
          title="Delete this roadmap"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '3px 6px',
            borderRadius: 4,
            border: 'none',
            background: 'none',
            color: MID_GRAY.lighter,
            cursor: isDeleting ? 'wait' : 'pointer',
          }}
        >
          {isDeleting ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <Trash2 style={{ width: 12, height: 12 }} />}
        </button>
      </div>
    </div>
  );
}
